import { createElement, useCallback, useMemo, useState } from 'react';
import { ChevronRight, ExternalLink, Mail, Phone } from 'lucide-react';
import type { DataTableColumn } from '@/shared/components/data-table';
import type { Supplier, SupplierKind, SupplierStatus } from '../types';
import { SUPPLIER_KINDS } from '../types';

export interface SupplierRenderContext {}

export type SupplierColumnDef = DataTableColumn<Supplier, SupplierRenderContext> & {
  defaultVisible?: boolean;
};

const ALL_COLUMN_KEYS = [
  'logo', 'accountNumber', 'name', 'kind', 'status',
  'website', 'email', 'phone', 'skus', 'gdrive', 'quickbooks', 'description', 'actions',
];

const VISIBILITY_KEY_BASE = 'atek-wms-suppliers-column-visibility';
const FILTERS_KEY_BASE = 'atek-wms-suppliers-column-filters';

const STATUS_OPTIONS: { label: string; value: SupplierStatus }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Suspended', value: 'suspended' },
];

const KIND_OPTIONS: { label: string; value: SupplierKind }[] = [
  { label: 'Hardware', value: 'hardware' },
  { label: 'Software', value: 'software' },
  { label: 'Service', value: 'service' },
  { label: 'Admin', value: 'admin' },
  { label: 'Carrier', value: 'carrier' },
  { label: 'Other', value: 'other' },
];

export type SuppliersView = 'wms' | 'accounting';

function defaultVisibility(view: SuppliersView): Record<string, boolean> {
  const base = view === 'wms'
    ? new Set(['logo', 'accountNumber', 'name', 'website', 'email', 'phone', 'skus', 'gdrive', 'quickbooks', 'actions'])
    : new Set(['logo', 'accountNumber', 'name', 'kind', 'status', 'website', 'email', 'phone', 'gdrive', 'quickbooks', 'actions']);
  const out: Record<string, boolean> = {};
  for (const key of ALL_COLUMN_KEYS) out[key] = base.has(key);
  return out;
}

function loadVisibility(view: SuppliersView): Record<string, boolean> {
  const key = `${VISIBILITY_KEY_BASE}-${view}`;
  try {
    const stored = localStorage.getItem(key);
    if (stored) return { ...defaultVisibility(view), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return defaultVisibility(view);
}

function loadFilters(view: SuppliersView): Record<string, string> {
  const key = `${FILTERS_KEY_BASE}-${view}`;
  try { return JSON.parse(localStorage.getItem(key) || '{}'); }
  catch { return {}; }
}

function domainOf(input: string | null | undefined): string | null {
  if (!input) return null;
  try {
    const u = new URL(input.startsWith('http') ? input : `https://${input}`);
    return u.hostname.replace(/^www\./, '');
  } catch { return null; }
}

function faviconUrl(supplier: Supplier): string | null {
  const dom = domainOf(supplier.website) ?? domainOf(supplier.email?.split('@')[1] ?? null);
  return dom ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(dom)}&sz=64` : null;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
}

function buildColumns(): SupplierColumnDef[] {
  return [
    {
      key: 'logo',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: (s) => {
        const url = faviconUrl(s);
        if (url) {
          return createElement(
            'div',
            { className: 'h-7 w-7 rounded border bg-white flex items-center justify-center overflow-hidden' },
            createElement('img', {
              src: url,
              alt: '',
              className: 'h-5 w-5',
              onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              },
            }),
          );
        }
        return createElement(
          'div',
          { className: 'h-7 w-7 rounded border bg-muted flex items-center justify-center text-[10px] font-semibold text-muted-foreground' },
          initials(s.name),
        );
      },
    },
    {
      key: 'accountNumber',
      label: '#',
      sortKey: 'account_number',
      filterType: 'text',
      getValue: (s) => s.account_number,
      render: (s) => createElement('span', { className: 'text-sm text-muted-foreground tabular-nums' }, s.account_number),
    },
    {
      key: 'name',
      label: 'Name',
      sortKey: 'name',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search supplier...',
      getValue: (s) => s.name,
      render: (s) => createElement('span', { className: 'font-medium' }, s.name),
    },
    {
      key: 'kind',
      label: 'Kind',
      sortKey: 'supplier_kind',
      filterType: 'select',
      filterOptions: KIND_OPTIONS,
      filterAllLabel: 'All Kinds',
      getValue: (s) => s.supplier_kind ?? '',
      render: (s) => createElement(
        'span',
        { className: 'capitalize text-sm' },
        s.supplier_kind ?? createElement('span', { className: 'text-muted-foreground' }, '—'),
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortKey: 'status',
      filterType: 'select',
      filterOptions: STATUS_OPTIONS,
      filterAllLabel: 'All Status',
      getValue: (s) => s.status,
      render: (s) => createElement('span', { className: 'capitalize text-sm' }, s.status),
    },
    {
      key: 'website',
      label: 'Website',
      filterType: 'text',
      getValue: (s) => s.website ?? '',
      render: (s) => {
        if (!s.website) return createElement('span', { className: 'text-muted-foreground text-sm' }, '—');
        const dom = domainOf(s.website) ?? s.website;
        const href = s.website.startsWith('http') ? s.website : `https://${s.website}`;
        return createElement(
          'a',
          {
            href,
            target: '_blank',
            rel: 'noopener noreferrer',
            className: 'inline-flex items-center gap-1 text-blue-600 hover:underline text-sm',
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          },
          dom,
          createElement(ExternalLink, { className: 'h-3 w-3' }),
        );
      },
    },
    {
      key: 'email',
      label: 'Email',
      filterType: 'text',
      getValue: (s) => s.email ?? '',
      render: (s) => s.email
        ? createElement(
            'a',
            {
              href: `mailto:${s.email}`,
              className: 'inline-flex items-center gap-1 text-blue-600 hover:underline text-sm',
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            },
            createElement(Mail, { className: 'h-3 w-3' }),
            s.email,
          )
        : createElement('span', { className: 'text-muted-foreground text-sm' }, '—'),
    },
    {
      key: 'phone',
      label: 'Phone',
      filterType: 'text',
      getValue: (s) => s.phone ?? '',
      render: (s) => s.phone
        ? createElement(
            'a',
            {
              href: `tel:${s.phone}`,
              className: 'inline-flex items-center gap-1 text-blue-600 hover:underline text-sm',
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            },
            createElement(Phone, { className: 'h-3 w-3' }),
            s.phone,
          )
        : createElement('span', { className: 'text-muted-foreground text-sm' }, '—'),
    },
    {
      key: 'skus',
      label: 'SKUs',
      sortKey: 'sku_count',
      filterType: 'text',
      getValue: (s) => (s.skus ?? []).map((sk) => sk.sku).join(' '),
      render: (s) => {
        const skus = s.skus ?? [];
        if (skus.length === 0) return createElement('span', { className: 'text-muted-foreground text-sm' }, '—');
        const max = 3;
        const visible = skus.slice(0, max);
        const overflow = skus.length - max;
        return createElement(
          'div',
          {
            className: 'flex flex-wrap gap-1 max-w-[280px]',
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            title: skus.map((sk) => sk.sku).join(', '),
          },
          ...visible.map((sk) => createElement(
            'a',
            {
              key: sk.id,
              href: `/skus?id=${encodeURIComponent(sk.id)}`,
              className: 'inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-mono hover:bg-blue-100 hover:underline',
            },
            sk.sku,
          )),
          overflow > 0 && createElement(
            'a',
            {
              key: 'more',
              href: `/wms/skus?supplier=${encodeURIComponent(s.id)}`,
              className: 'inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[11px] hover:underline',
            },
            `+${overflow}`,
          ),
        );
      },
    },
    {
      key: 'gdrive',
      label: 'Drive',
      noFilter: true,
      getValue: (s) => (s.gdrive_root_folder_id ? 'yes' : ''),
      render: (s) => s.gdrive_root_folder_id
        ? createElement(
            'a',
            {
              href: `https://drive.google.com/drive/folders/${encodeURIComponent(s.gdrive_root_folder_id)}`,
              target: '_blank',
              rel: 'noopener noreferrer',
              className: 'inline-flex items-center gap-1 text-blue-600 hover:underline text-sm',
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            },
            'Drive',
            createElement(ExternalLink, { className: 'h-3 w-3' }),
          )
        : createElement('span', { className: 'text-muted-foreground text-sm' }, '—'),
    },
    {
      key: 'quickbooks',
      label: 'QB',
      noFilter: true,
      getValue: (s) => (s.quickbooks_vendor_id ? 'yes' : ''),
      render: (s) => s.quickbooks_vendor_id
        ? createElement(
            'a',
            {
              href: `https://qbo.intuit.com/app/vendordetail?nameId=${encodeURIComponent(s.quickbooks_vendor_id)}`,
              target: '_blank',
              rel: 'noopener noreferrer',
              className: 'inline-flex items-center gap-1 text-blue-600 hover:underline text-sm',
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
            },
            'QB',
            createElement(ExternalLink, { className: 'h-3 w-3' }),
          )
        : createElement('span', { className: 'text-muted-foreground text-sm' }, '—'),
    },
    {
      key: 'description',
      label: 'Description',
      filterType: 'text',
      getValue: (s) => stripHtml(s.description ?? ''),
      render: (s) => createElement(
        'span',
        { className: 'text-sm text-muted-foreground line-clamp-1', title: stripHtml(s.description ?? '') },
        stripHtml(s.description ?? '') || '—',
      ),
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: () => createElement(ChevronRight, { className: 'h-4 w-4 text-gray-400' }),
    },
  ];
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function escapeCSV(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportSuppliersCSV(items: Supplier[], columns: SupplierColumnDef[], ctx: SupplierRenderContext) {
  const cols = columns.filter((c) => !c.noFilter || c.key === 'skus');
  const header = cols.map((c) => escapeCSV(c.label || c.key)).join(',');
  const rows = items.map((s) => cols.map((c) => escapeCSV(c.getValue(s, ctx))).join(','));
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `suppliers_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function copySuppliersToClipboard(items: Supplier[], columns: SupplierColumnDef[], ctx: SupplierRenderContext) {
  const cols = columns.filter((c) => !c.noFilter || c.key === 'skus');
  const header = cols.map((c) => c.label || c.key).join('\t');
  const rows = items.map((s) => cols.map((c) => String(c.getValue(s, ctx) ?? '')).join('\t'));
  return navigator.clipboard.writeText([header, ...rows].join('\n'));
}

export interface UseSupplierListTableResult {
  sortKey: string;
  sortDirection: 1 | -1;
  columnFilters: Record<string, string>;
  columnVisibility: Record<string, boolean>;
  columns: SupplierColumnDef[];
  visibleColumns: SupplierColumnDef[];
  ctx: SupplierRenderContext;
  sortedSuppliers: Supplier[];
  totalCount: number;
  isVisible: (key: string) => boolean;
  toggleColumn: (key: string) => void;
  setColumnFilter: (key: string, value: string) => void;
  handleSort: (key: string) => void;
}

export function useSupplierListTable(suppliers: Supplier[], view: SuppliersView = 'wms'): UseSupplierListTableResult {
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => loadFilters(view));
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => loadVisibility(view));

  const ctx: SupplierRenderContext = useMemo(() => ({}), []);
  const columns = useMemo(() => buildColumns(), []);

  const isVisible = useCallback(
    (key: string) => columnVisibility[key] !== false,
    [columnVisibility],
  );

  const visibleColumns = useMemo(
    () => columns.filter((col) => columnVisibility[col.key] !== false),
    [columns, columnVisibility],
  );

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem(`${VISIBILITY_KEY_BASE}-${view}`, JSON.stringify(updated));
      return updated;
    });
  }, [view]);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(`${FILTERS_KEY_BASE}-${view}`, JSON.stringify(next));
      return next;
    });
  }, [view]);

  const filtered = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return suppliers;
    return suppliers.filter((s) => active.every(([colKey, filterValue]) => {
      const col = columns.find((c) => c.key === colKey);
      if (!col || col.noFilter) return true;
      const raw = col.getValue(s, ctx);
      if (col.filterType === 'select') {
        try {
          const selected = JSON.parse(filterValue) as string[];
          return selected.length === 0 || selected.includes(String(raw || ''));
        } catch { return true; }
      }
      return String(raw ?? '').toLowerCase().includes(filterValue.toLowerCase());
    }));
  }, [suppliers, columnFilters, columns, ctx]);

  const sortedSuppliers = useMemo(() => {
    const col = columns.find((c) => c.sortKey === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const aRaw = col.getValue(a, ctx);
      const bRaw = col.getValue(b, ctx);
      // numeric compare for sku_count
      if (sortKey === 'sku_count') {
        return (Number(aRaw) - Number(bRaw)) * sortDirection;
      }
      const aVal = String(aRaw ?? '');
      const bVal = String(bRaw ?? '');
      return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' }) * sortDirection;
    });
  }, [filtered, sortKey, sortDirection, columns, ctx]);

  const handleSort = useCallback((key: string) => {
    if (sortKey === key) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(key); setSortDirection(1); }
  }, [sortKey]);

  return {
    sortKey, sortDirection, columnFilters, columnVisibility,
    columns, visibleColumns, ctx, sortedSuppliers,
    totalCount: suppliers.length,
    isVisible, toggleColumn, setColumnFilter, handleSort,
  };
}

export { SUPPLIER_KINDS, STATUS_OPTIONS, KIND_OPTIONS };
